import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { clientAuthService, ClientUser, RegisterPendingResult } from "../services/client-auth.service";
import { apiService } from "../services/api.service";

interface AuthContextType {
  client: ClientUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ mfaRequired?: boolean; mfaToken?: string }>;
  verifyMFA: (mfaToken: string, code: string) => Promise<void>;
  registerPending: (data: {
    email: string;
    name: string;
    password: string;
    phone?: string;
  }) => Promise<RegisterPendingResult>;
  resendVerificationCode: (email: string) => Promise<{ message: string; expiresInSeconds: number }>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<ClientUser | null>(apiService.getClient());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      if (apiService.getToken()) {
        const me = await clientAuthService.fetchMe();
        if (!me) {
          apiService.clearSession();
          setClient(null);
        } else {
          setClient(me);
        }
      }
      setIsLoading(false);
    };
    init();
  }, []);

  const login: AuthContextType["login"] = async (email, password) => {
    const result = await clientAuthService.login(email, password);
    if (result.mfaRequired) {
      return { mfaRequired: true, mfaToken: result.mfaToken };
    }
    setClient(result.data!.user);
    return {};
  };

  const verifyMFA: AuthContextType["verifyMFA"] = async (mfaToken, code) => {
    const result = await clientAuthService.verifyMFA(mfaToken, code);
    setClient(result.data!.user);
  };

  const registerPending: AuthContextType["registerPending"] = async (data) => {
    return await clientAuthService.registerPending(data);
  };

  const resendVerificationCode: AuthContextType["resendVerificationCode"] = async (email) => {
    return await clientAuthService.resendVerificationCode(email);
  };

  const verifyEmail: AuthContextType["verifyEmail"] = async (email, code) => {
    const result = await clientAuthService.verifyEmail(email, code);
    setClient(result.data!.user);
  };

  const logout = async () => {
    await clientAuthService.logout();
    setClient(null);
  };

  return (
    <AuthContext.Provider
      value={{
        client,
        isAuthenticated: !!client && !!apiService.getToken(),
        isLoading,
        login,
        verifyMFA,
        registerPending,
        resendVerificationCode,
        verifyEmail,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}